/**
 * src/lib/fundModel.js — mô hình dữ liệu nguồn vốn cho trang quản trị /admin/nguon-von.
 *
 * Hàm thuần, chạy được cả trình duyệt lẫn Node (test, script build).
 *
 * Trạng thái (giữ nguyên trường publish.status đang dùng trong src/data/funds.js):
 *   DRAFT     — đang soạn, chỉ người quản trị thấy.
 *   PUBLISHED — hiện trên website và trong công cụ đối chiếu.
 *   HIDDEN    — ẩn khỏi website, kể cả quỹ có sẵn trong code.
 *
 * Điều kiện bắt buộc (thesis.hardRequirements) là luật máy dùng để đối chiếu hồ
 * sơ, gồm trường dữ liệu và phép so sánh. Trang quản trị GIỮ NGUYÊN chúng, không
 * cho sửa, vì sửa sai sẽ loại nhầm founder. Muốn đổi, cần người kỹ thuật.
 */
import { isValid } from '../../shared/taxonomy.js';

export const TRANG_THAI_QUY = ['DRAFT', 'PUBLISHED', 'HIDDEN'];

export const CO_SO_TICKET = [
  { value: 'INITIAL_CHECK', label: 'Séc đầu tư ban đầu quỹ công bố' },
  { value: 'OBSERVED_DEALS', label: 'Suy từ các thương vụ đã công bố' },
];
export const LOAI_NGUON_TICKET = [
  { value: 'OFFICIAL', label: 'Quỹ tự công bố' },
  { value: 'OFFICIAL_STALE', label: 'Quỹ công bố đã lâu' },
  { value: 'PRESS', label: 'Báo chí' },
  { value: 'THIRD_PARTY_ESTIMATE', label: 'Ước tính của bên thứ ba' },
];
export const DO_TIN_CAY = [
  { value: 'HIGH', label: 'Cao' },
  { value: 'MEDIUM', label: 'Trung bình' },
  { value: 'LOW', label: 'Thấp' },
];

/** Khoá được phép lưu trên Firestore — khớp với firestore.rules. */
export const KHOA_QUY = [
  'fundId', 'slug', 'name', 'shortName', 'type', 'vietnamPresence', 'vietnamEvidence',
  'website', 'publish', 'thesis', 'process', 'content', 'portfolio', 'provenance',
  'updatedAt', 'updatedBy',
];

const LA_NGAY = /^\d{4}-\d{2}-\d{2}$/;
const chuoi = (v, max = 300) => (typeof v === 'string' ? v.trim().slice(0, max) : '');
const ngayHoacNull = (v) => (LA_NGAY.test(String(v || '')) ? String(v) : null);
const httpsUrl = (v) => {
  const s = chuoi(v, 600);
  return /^https:\/\/\S+$/i.test(s) ? s : '';
};
const danhSach = (v, soMuc = 12, max = 400) =>
  (Array.isArray(v) ? v : String(v || '').split('\n'))
    .map((x) => chuoi(x, max)).filter(Boolean).slice(0, soMuc);
const tapHopLe = (v, taxonomy) =>
  [...new Set((Array.isArray(v) ? v : []).filter((x) => isValid(taxonomy, x)))];

export function chuanHoaSlug(s) {
  return String(s || '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/đ/gi, 'd')
    .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 80);
}

/* ------------------------------------------------------------------ */
/* Tiền: form nhập theo đơn vị lớn, dữ liệu lưu dạng Money              */
/* ------------------------------------------------------------------ */

const DON_VI_NHO = { VND: 1, USD: 100 };

/** { so: 200000, ccy: 'USD', fx: 26000, fxDate } → Money, hoặc null nếu trống. */
export function tienTuForm({ so, ccy = 'USD', fx, fxDate } = {}) {
  const n = Number(String(so ?? '').replace(/[^\d.]/g, ''));
  if (!so || !Number.isFinite(n) || n <= 0) return null;
  if (!DON_VI_NHO[ccy]) return null;
  const amount = Math.round(n * DON_VI_NHO[ccy]);
  if (ccy === 'VND') return { amount, ccy, fx: null, fxDate: null };
  const tyGia = Number(fx);
  if (!Number.isFinite(tyGia) || tyGia <= 0) return null;
  return { amount, ccy, fx: tyGia, fxDate: ngayHoacNull(fxDate) };
}

/** Money → số theo đơn vị lớn để hiện trên form. */
export function soTuTien(m) {
  if (!m || !DON_VI_NHO[m.ccy]) return '';
  return String(m.amount / DON_VI_NHO[m.ccy]);
}

function tienHopLe(m) {
  if (!m || typeof m !== 'object') return null;
  if (!Number.isInteger(m.amount) || !DON_VI_NHO[m.ccy]) return null;
  if (m.ccy !== 'VND' && !(Number(m.fx) > 0)) return null;
  return { amount: m.amount, ccy: m.ccy, fx: m.ccy === 'VND' ? null : Number(m.fx), fxDate: m.ccy === 'VND' ? null : ngayHoacNull(m.fxDate) };
}


/* ------------------------------------------------------------------ */
/* Danh mục đã đầu tư                                                   */
/* ------------------------------------------------------------------ */

/**
 * Vì sao khối này tồn tại: đọc hết một trang quỹ xong founder vẫn không biết
 * "quỹ này muốn mình giống ai". Danh mục trả lời câu đó nhanh hơn mọi đoạn mô tả.
 *
 * Hai quy tắc không được phá:
 *
 * 1. Con số ở đây là TỔNG VÒNG ĐƯỢC CÔNG BỐ, KHÔNG phải séc của quỹ này. Một
 *    vòng 5 triệu USD có bốn nhà đầu tư thì quỹ này có thể chỉ viết 1 triệu.
 *    Vì vậy `roundSize` chỉ được giữ khi đi kèm `roundSource`; thiếu nguồn thì
 *    bỏ số, không đoán. Ghi nhầm một con số ở đây là dựng mỏ neo sai cho cả
 *    vòng gọi vốn của người đọc.
 *
 * 2. Chỉ ghi doanh nghiệp quỹ tự nêu trên trang chính thức, hoặc báo chí nêu rõ
 *    quỹ có tham gia vòng đó. Không suy từ danh sách tổng hợp của bên thứ ba —
 *    cùng quy tắc với phần còn lại của src/data/funds.js.
 */
export const KET_QUA_DANH_MUC = [
  { value: 'ACQUIRED', label: 'Đã được mua lại' },
  { value: 'IPO', label: 'Đã niêm yết' },
  { value: 'FOLLOW_ON', label: 'Quỹ rót tiếp vòng sau' },
  { value: 'EXITED', label: 'Quỹ đã thoái vốn' },
];

/** Công bố thương vụ nhiều khi chỉ có tháng hoặc năm, không có ngày. */
const LA_NGAY_HOAC_THANG = /^\d{4}(-\d{2}(-\d{2})?)?$/;
const ngayHoacThang = (v) => (LA_NGAY_HOAC_THANG.test(String(v || '')) ? String(v) : null);

function chuanHoaDongDanhMuc(raw = {}) {
  const ten = chuoi(raw.name, 120);
  if (!ten) return null;
  const size = tienHopLe(raw.roundSize);
  const nguonSo = httpsUrl(raw.roundSource);
  return {
    name: ten,
    url: httpsUrl(raw.url),
    whatTheyDo: chuoi(raw.whatTheyDo, 200),
    industries: tapHopLe(raw.industries, 'INDUSTRY').slice(0, 3),
    stage: isValid('STAGE', raw.stage) ? raw.stage : '',
    geo: isValid('GEO', raw.geo) ? raw.geo : '',
    roundDate: ngayHoacThang(raw.roundDate),
    // Chốt chặn của quy tắc 1: không nguồn thì không có số.
    ...(size && nguonSo ? { roundSize: size, roundSource: nguonSo } : {}),
    ...(typeof raw.leadInvestor === 'boolean' ? { leadInvestor: raw.leadInvestor } : {}),
    ...(KET_QUA_DANH_MUC.some((x) => x.value === raw.outcome) ? { outcome: raw.outcome } : {}),
    outcomeNote: chuoi(raw.outcomeNote, 200),
  };
}

export function chuanHoaDanhMuc(raw) {
  const items = (Array.isArray(raw?.items) ? raw.items : [])
    .map(chuanHoaDongDanhMuc).filter(Boolean).slice(0, 12);
  if (!items.length) return null;
  return {
    items,
    note: chuoi(raw?.note, 400),
    source: httpsUrl(raw?.source),
    verifiedAt: ngayHoacNull(raw?.verifiedAt),
  };
}

/**
 * Một dòng trả lời "quỹ muốn mình giống ai", SUY TỪ chính các dòng danh mục chứ
 * không phải câu người quản trị tự viết — nhờ vậy nó không bao giờ lệch khỏi dữ
 * liệu bên dưới. Trả null khi danh mục quá mỏng để kết luận (dưới 3 doanh nghiệp).
 *
 * Ngưỡng: một ngành chỉ được nêu khi chiếm ít nhất 25% danh mục và có từ 2 doanh
 * nghiệp trở lên — đủ để gọi là một khuynh hướng, chưa đủ để gọi là chuyên ngành,
 * nên câu chữ ở fundDisplay.js chỉ nói "xuất hiện nhiều nhất".
 */
export function tomTatDanhMuc(f) {
  // Dùng chung cho quỹ (portfolio) và chương trình (alumni): cùng một hình dạng
  // dữ liệu, cùng một cách suy khuynh hướng, nên không tách thành hai bản luật.
  const items = f?.portfolio?.items || f?.alumni?.items || [];
  if (items.length < 3) return null;
  const dem = (lay) => {
    const m = new Map();
    for (const it of items) for (const v of lay(it)) m.set(v, (m.get(v) || 0) + 1);
    return [...m.entries()].sort((a, b) => b[1] - a[1] || String(a[0]).localeCompare(b[0]));
  };
  const nguong = Math.max(2, Math.ceil(items.length * 0.25));
  const loc = (cap, soMuc) => cap.filter(([, n]) => n >= nguong).slice(0, soMuc).map(([id]) => id);
  return {
    soCty: items.length,
    nganh: loc(dem((it) => it.industries || []), 3),
    vong: loc(dem((it) => (it.stage ? [it.stage] : [])), 2),
    soVN: items.filter((it) => it.geo === 'VN').length,
    soCoSo: items.filter((it) => it.roundSize).length,
    soKetQua: items.filter((it) => it.outcome).length,
  };
}

/* ------------------------------------------------------------------ */

export function chuanHoaQuy(raw = {}) {
  const th = raw.thesis || {};
  const tk = th.ticket || {};
  const slug = chuanHoaSlug(raw.slug || raw.fundId || raw.name);
  const status = TRANG_THAI_QUY.includes(raw.publish?.status) ? raw.publish.status : 'DRAFT';
  const min = tienHopLe(tk.min);
  const max = tienHopLe(tk.max);
  // basis / sourceType / confidence chỉ giữ khi hợp lệ, KHÔNG tự gán mặc định:
  // engine đối chiếu dựa vào sourceType để quyết định có loại cứng theo séc hay không.
  const ticket = (min || max) ? {
    min, max,
    ...(CO_SO_TICKET.some((x) => x.value === tk.basis) ? { basis: tk.basis } : {}),
    ...(LOAI_NGUON_TICKET.some((x) => x.value === tk.sourceType) ? { sourceType: tk.sourceType } : {}),
    ...(DO_TIN_CAY.some((x) => x.value === tk.confidence) ? { confidence: tk.confidence } : {}),
    ...(typeof tk.hardFilter === 'boolean' ? { hardFilter: tk.hardFilter } : {}),
    ...(ngayHoacNull(tk.verifiedAt) ? { verifiedAt: tk.verifiedAt } : {}),
  } : null;
  const ctaType = ['apply', 'contact'].includes(raw.process?.ctaType) ? raw.process.ctaType : '';
  return {
    fundId: chuoi(raw.fundId, 80) || slug,
    slug,
    name: chuoi(raw.name, 180),
    shortName: chuoi(raw.shortName, 60),
    type: isValid('FUND_TYPE', raw.type) ? raw.type : 'VC',
    vietnamPresence: isValid('VN_PRESENCE', raw.vietnamPresence) ? raw.vietnamPresence : '',
    vietnamEvidence: chuoi(raw.vietnamEvidence, 500),
    website: httpsUrl(raw.website),
    publish: { status },
    thesis: {
      capitalTypes: tapHopLe(th.capitalTypes, 'CAPITAL_TYPE'),
      industries: tapHopLe(th.industries, 'INDUSTRY'),
      stages: tapHopLe(th.stages, 'STAGE'),
      geos: tapHopLe(th.geos, 'GEO'),
      businessModels: tapHopLe(th.businessModels, 'BUSINESS_MODEL'),
      customerTypes: tapHopLe(th.customerTypes, 'CUSTOMER_TYPE'),
      instruments: tapHopLe(th.instruments, 'INSTRUMENT'),
      ...(ticket ? { ticket } : {}),
      ...(typeof th.requiresRevenue === 'boolean' ? { requiresRevenue: th.requiresRevenue } : {}),
      ...(typeof th.requiresIncorporation === 'boolean' ? { requiresIncorporation: th.requiresIncorporation } : {}),
      // Luật máy — giữ nguyên, không sửa trên form.
      hardRequirements: Array.isArray(th.hardRequirements) ? th.hardRequirements : [],
    },
    process: {
      submitUrl: httpsUrl(raw.process?.submitUrl) || null,
      submitEmail: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(chuoi(raw.process?.submitEmail, 160)) ? chuoi(raw.process.submitEmail, 160) : '',
      ...(ctaType ? { ctaType } : {}),
      notes: chuoi(raw.process?.notes, 400),
    },
    content: {
      summary: chuoi(raw.content?.summary, 600),
      lookingFor: danhSach(raw.content?.lookingFor, 10, 300),
      tips: danhSach(raw.content?.tips, 8, 400),
    },
    ...(chuanHoaDanhMuc(raw.portfolio) ? { portfolio: chuanHoaDanhMuc(raw.portfolio) } : {}),
    provenance: {
      sources: (Array.isArray(raw.provenance?.sources) ? raw.provenance.sources : [])
        .map((s) => ({ url: httpsUrl(s?.url), fetchedAt: ngayHoacNull(s?.fetchedAt), note: chuoi(s?.note, 200) }))
        .filter((s) => s.url).slice(0, 10),
      lastVerifiedAt: ngayHoacNull(raw.provenance?.lastVerifiedAt),
    },
    updatedAt: chuoi(raw.updatedAt, 40),
    updatedBy: chuoi(raw.updatedBy, 200),
  };
}

/** Lỗi phải sửa trước khi XUẤT BẢN — cùng chuẩn với scripts/check-funds.mjs. */
export function kiemTraQuy(f) {
  const loi = [];
  if (!f.name) loi.push('Thiếu tên quỹ.');
  if (!f.slug) loi.push('Thiếu đường dẫn (slug).');
  if (!f.thesis?.capitalTypes?.length) loi.push('Chọn ít nhất một loại vốn.');
  if (!f.vietnamPresence) loi.push('Chọn mức hiện diện tại Việt Nam.');
  if (['INVESTED', 'ANNOUNCED'].includes(f.vietnamPresence) && !f.vietnamEvidence) {
    loi.push('Mức hiện diện này cần ghi căn cứ (đã đầu tư vào đâu, công bố ở đâu).');
  }
  if (!f.process?.submitUrl && !f.process?.submitEmail) loi.push('Cần đường dẫn nộp hồ sơ / liên hệ hoặc email nhận hồ sơ.');
  if (!f.content?.summary) loi.push('Thiếu mô tả ngắn.');
  if (!f.provenance?.sources?.length) loi.push('Cần ít nhất một nguồn chính thức (https://…).');
  if (f.provenance?.sources?.some((s) => !s.fetchedAt)) loi.push('Mỗi nguồn cần ngày lấy thông tin.');
  if (!f.provenance?.lastVerifiedAt) loi.push('Thiếu ngày kiểm chứng.');
  const dm = f.portfolio;
  if (dm?.items?.length) {
    if (!dm.verifiedAt) loi.push('Danh mục đầu tư cần ngày kiểm chứng.');
    if (!dm.source) loi.push('Danh mục đầu tư cần đường dẫn nguồn (trang danh mục của quỹ).');
    if (dm.items.some((x) => !x.whatTheyDo)) loi.push('Mỗi doanh nghiệp trong danh mục cần một câu mô tả doanh nghiệp đó làm gì.');
    if (dm.items.some((x) => !x.industries?.length)) loi.push('Mỗi doanh nghiệp trong danh mục cần ít nhất một ngành.');
  }
  const t = f.thesis?.ticket;
  if (t?.min && t?.max && t.min.ccy === t.max.ccy && t.min.amount > t.max.amount) {
    loi.push('Séc tối thiểu đang lớn hơn tối đa.');
  }
  return loi;
}

/**
 * Gộp danh sách trong code với bản ghi trên Firestore theo fundId.
 * PUBLISHED thay thế, HIDDEN ẩn đi, DRAFT không ảnh hưởng website.
 */
export function gopQuy(goc = [], remote = []) {
  const theoId = new Map(goc.map((f) => [f.fundId, f]));
  for (const r of remote) {
    const tt = r?.publish?.status;
    if (!r?.fundId) continue;
    if (tt === 'HIDDEN') theoId.delete(r.fundId);
    else if (tt === 'PUBLISHED') theoId.set(r.fundId, r);
  }
  return [...theoId.values()];
}

/** Nhắc việc cho người quản trị. Trả về danh sách lý do (rỗng = ổn). */
export function canXemLaiQuy(f, homNay, soNgayCu = 180) {
  const lyDo = [];
  const v = f.provenance?.lastVerifiedAt;
  if (!v) lyDo.push('Chưa có ngày kiểm chứng');
  else {
    const d = (s) => Date.UTC(+s.slice(0, 4), +s.slice(5, 7) - 1, +s.slice(8, 10));
    if ((d(homNay) - d(v)) / 86400000 > soNgayCu) lyDo.push(`Kiểm chứng đã hơn ${Math.round(soNgayCu / 30)} tháng`);
  }
  if (f.process?.submitUrl && !f.process?.ctaType) lyDo.push('Chưa xác nhận nút Nộp hồ sơ / Liên hệ');
  return lyDo;
}
